import React from 'react'

/**
 * Sidebar surface tokens.
 *
 * Shared by the sidebar shell and the folder tree so the two can't drift.
 * Kept in its own module rather than on the sidebar itself because Folders is
 * a child of the sidebar — importing from the parent would be circular.
 *
 * The panel used to be painted with the hub's brand colour, so everything
 * inside it assumed white-on-dark. It is now a neutral shade off the canvas,
 * and the brand colour reads as an accent instead (the hub's initial square,
 * the active-row marker). See USE_HUB_COLOUR_SURFACE in the sidebar for the
 * opt-in path back to a branded panel.
 *
 * The values are CSS variables rather than hexes so the panel follows the
 * theme: they are consumed both as inline styles and as Tailwind classes, and
 * a variable is the one form that works in both.
 */

/** Panel background. */
export const SIDEBAR_SURFACE = 'var(--color-sidebar)'

/** Panel border, and dividers within it. */
export const SIDEBAR_BORDER = 'var(--color-sidebar-line)'

/** Default row text. Tracks the panel, so it inverts with it. */
export const SIDEBAR_TEXT = 'var(--color-sidebar-text)'

/** Hover fill for an interactive row. */
export const SIDEBAR_HOVER = 'var(--color-sidebar-hover)'

/** Fill for the row matching the current route. */
export const SIDEBAR_ACTIVE = 'var(--color-sidebar-active)'

/**
 * One row geometry for every item in the panel — top-level nav, hub list and
 * the resource tree alike. 32px tall with 13px text; selection reads as a
 * filled pill rather than an outline, because a 1px border on a row makes the
 * text inside shift by a pixel and reads as a bug.
 *
 * Horizontal padding is deliberately absent: tree rows set their own
 * `paddingLeft` to express depth. Pair with SIDEBAR_ROW_PAD at the top level.
 */
export const SIDEBAR_ROW =
  'flex items-center gap-2 w-full h-8 pr-2 rounded-md text-[13px] font-medium leading-[18px] cursor-pointer transition-colors select-none'

export const SIDEBAR_ROW_PAD = 'pl-2'
export const SIDEBAR_ROW_IDLE = 'text-body hover:bg-sidebar-hover hover:text-body'

/**
 * The icon slot on the left of every row.
 *
 * The `[&>svg]` rule is the point: some icon components accept a className and
 * some don't, so sizing them at the call site produced a 14px search icon next
 * to a natural-size document icon in identical 16px slots. Constraining the
 * child svg from the slot sizes every icon the same way regardless. 14px, at
 * Lucide's stroke, a line of about 1.17px: at 16px (1.33px lines) the icons
 * read large and soft beside the 13px labels.
 */
export const SIDEBAR_ICON =
  'flex items-center justify-center w-6 pl-1.5 h-[18px] shrink-0 opacity-70 [&>svg]:w-3.5 [&>svg]:h-3.5 [&_path[stroke]]:[stroke-width:2.4]'

/**
 * Badge size for a hub in a list, and the signed-in user's avatar.
 */
export const SIDEBAR_ICON_SIZE = 18

/**
 * The current hub's badge, heading its resource list — 50% larger than a list
 * badge so the hub you are in reads as the subject of the panel.
 *
 * Every icon slot is exactly this wide. Inside it, a fixed 18px icon box is
 * pinned to the right (the slot's 6px left padding), and the glyph is centred
 * in that box. Three lines stay straight at once: every label starts at the
 * same x as the hub's name; every box ends the same 8px before its label; and
 * every glyph shares one centre line whatever its width. Right-aligning the
 * glyphs themselves would line up their right edges but stagger their centres
 * — a 13px table icon sat 1.5px off a 16px document icon. Change this and the
 * slot width must follow, or the labels drift off the hub's name.
 */
export const SIDEBAR_HERO_BADGE = 24

/** Wraps anything narrower than a slot (list badges, avatars) so it aligns. */
export const SIDEBAR_SLOT = 'flex items-center justify-center w-6 pl-1.5 shrink-0'

/**
 * Middle density. Plus fills ~86% of its box against Docs's ~73%, so it needs
 * trimming, but less than a full-bleed glyph like Spreadsheet.
 */
export const SIDEBAR_ICON_SEMI =
  'flex items-center justify-center w-6 pl-1.5 h-[18px] shrink-0 opacity-70 [&>svg]:w-[14px] [&>svg]:h-[14px] [&_path[stroke]]:[stroke-width:2.4]'

/**
 * A header action (add, settings). Colour-only hover — the row beneath already
 * uses a filled hover, and a second filled target in the same row competes
 * with it. The tooltip carries the label.
 */
export const SIDEBAR_ACTION =
  'flex items-center justify-center w-6 h-6 rounded-md bg-transparent border-0 p-0 text-body opacity-70 hover:opacity-100 hover:text-blue hover:bg-transparent transition-[color,opacity] [&>svg]:w-4 [&>svg]:h-4 [&_path[stroke]]:[stroke-width:2.4]'

/**
 * The row's text.
 *
 * Weight lives here rather than on the row because style.scss carries a global
 * `a, a:link, ...` rule setting font-weight 400. That selector is specificity
 * (0,1,1) and beats a utility class, so a `font-medium` on a <Link> row is
 * silently ignored — which is why the resource tree rendered lighter than the
 * nav above it. A span is unaffected by that rule.
 */
export const sidebarLabel = (active?: boolean): string =>
  `truncate min-w-0 flex-1 text-left ${active ? 'font-semibold' : 'font-medium'}`

/**
 * A secondary row — "back", and the create actions at the foot of the list.
 * One step quieter than a content row so navigation and creation sit behind
 * the things they act on.
 */
export const SIDEBAR_ROW_MUTED = 'text-muted hover:bg-sidebar-hover hover:text-body'

/** Horizontal padding for a header row, matching a normal row's. */
export const SIDEBAR_HEADER_PAD = 'px-2'

/**
 * The selected row. Colour is applied inline from the hub's brand colour
 * rather than baked in here, so selection is the second place that colour
 * shows up (after the hub badge) — see SIDEBAR_ACTIVE_STYLE.
 */
export const SIDEBAR_ROW_ACTIVE = 'bg-sidebar-active font-semibold'

/** The brand colour for a hub that hasn't set one. */
const DEFAULT_HUB_COLOUR = '#2B3389'

/**
 * The hub's brand colour as text that is readable in the current theme: same
 * hue, lightness pinned per theme by --hub-text-l in style/tailwind.css. The raw
 * brand colour is still used where it sits on its own fill (the hub badge) or
 * as a faint tint (the wash), since neither is text against the sidebar.
 */
export const brandText = (brandColour?: string): string =>
  `oklch(from ${brandColour || DEFAULT_HUB_COLOUR} var(--hub-text-l) min(c, var(--hub-text-c)) h)`

/**
 * Inline half of the selected-row treatment: brand-coloured label, and a wash
 * of the brand colour over the neutral active fill.
 *
 * The wash is a background-image layered over a neutral base. Both the base
 * and the wash strength are theme tokens (--hub-row-base, --hub-wash in
 * style/tailwind.css): the light theme wants a paler base than the dark one,
 * because a mid-grey behind dark text costs more legibility than a faint white
 * behind light text. That keeps the neutral step that
 * works in both themes — a light grey on the light sidebar, a faint white on
 * the dark one — and adds only a hint of hue, so a green hub's selected row
 * reads green without becoming a coloured block.
 */
export const activeRowStyle = (brandColour?: string): React.CSSProperties => {
  const brand = brandColour || DEFAULT_HUB_COLOUR
  const wash = `color-mix(in srgb, ${brand} var(--hub-wash), transparent)`
  return {
    color: brandText(brand),
    backgroundColor: 'var(--hub-row-base)',
    backgroundImage: `linear-gradient(${wash}, ${wash})`
  }
}

/**
 * A row in the sidebar's multi-selection: the active-row treatment at lower strength and without
 * the brand-coloured label, so a selection reads as a group without competing with the row for
 * the page on screen.
 */
const selectionWash = (brand: string): string => {
  const wash = `color-mix(in srgb, ${brand} calc(var(--hub-wash) * 0.6), transparent)`
  return `linear-gradient(${wash}, ${wash})`
}

const selectedRowStyle = (brand: string): React.CSSProperties => ({
  backgroundColor: 'color-mix(in srgb, var(--hub-row-base) 60%, transparent)',
  backgroundImage: selectionWash(brand)
})

export interface RowHighlight {
  /** The row for the page on screen. */
  active?: boolean
  /** Part of the sidebar's multi-selection. */
  selected?: boolean
  /** A folder something is being dragged into. */
  dropTarget?: boolean
  /** What a destructive action would take away, while the viewer points at it: a red wash. */
  previewed?: boolean
  /** Which neighbours of a selected or previewed row are marked too, so the run draws as one block. */
  join?: SelectionJoin
}

/** The wash on rows a destructive action would take away, over any other highlight. */
const previewedRowStyle: React.CSSProperties = {
  backgroundColor: 'color-mix(in srgb, var(--color-red) 14%, transparent)',
  backgroundImage: 'none'
}

/**
 * A 2px red outline round the block of rows a destructive action would take away, so it shows
 * even on a hub whose own colour is red. Each row draws its sides, and the top or bottom edge only
 * where the block starts or ends, so a run of rows gets one outline rather than a box each.
 */
const previewOutline = (join: SelectionJoin): string => {
  const red = 'var(--color-red)'
  const edges = [`inset 2px 0 0 ${red}`, `inset -2px 0 0 ${red}`]
  if (join !== 'above' && join !== 'both') edges.push(`inset 0 2px 0 ${red}`)
  if (join !== 'below' && join !== 'both') edges.push(`inset 0 -2px 0 ${red}`)
  return edges.join(', ')
}

/**
 * Which of a selected row's neighbours on screen are selected too: the row squares off the corners
 * it shares with them, so a run of selected rows reads as one block, as in Finder.
 */
export type SelectionJoin = 'none' | 'above' | 'below' | 'both'

const joinCorners = (join: SelectionJoin): React.CSSProperties => ({
  ...(join === 'above' || join === 'both' ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : {}),
  ...(join === 'below' || join === 'both' ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : {})
})

const baseHighlight = (active: boolean, selected: boolean, brand: string): React.CSSProperties => {
  // Inside a selection the open page has the selection's fill, so the block is one colour; its
  // label keeps the brand colour, so the page on screen can still be found.
  if (active && selected) return { ...selectedRowStyle(brand), color: brandText(brand) }
  if (active) return activeRowStyle(brand)
  if (selected) return selectedRowStyle(brand)
  return {}
}

/**
 * The ring round a folder, or the whole tree, that a drag would drop into. In brandText rather than
 * the raw brand colour, which can all but vanish against the sidebar (a dark brand on the dark theme).
 */
export const dropRingStyle = (brandColour?: string): React.CSSProperties => ({
  boxShadow: `inset 0 0 0 1px ${brandText(brandColour)}`
})

/**
 * The inline highlight for a tree row. A selected or previewed row squares the corners it shares
 * with marked neighbours (`join`); a drop target adds the drop ring to any state.
 */
export const rowHighlightStyle = (
  { active = false, selected = false, previewed = false, dropTarget = false, join = 'none' }: RowHighlight,
  brandColour?: string
): React.CSSProperties => {
  const base = baseHighlight(active, selected, brandColour || DEFAULT_HUB_COLOUR)
  const style = previewed ? { ...base, ...previewedRowStyle, boxShadow: previewOutline(join) } : base
  const joined = selected || previewed ? { ...style, ...joinCorners(join) } : style
  return dropTarget ? { ...joined, ...dropRingStyle(brandColour) } : joined
}

/** The line a drag draws above or below a row to show it would drop there. Its row is `relative`. */
export const SIDEBAR_DROP_LINE = 'pointer-events-none absolute right-1 h-0.5 rounded-full'

/** Where the drop line sits: on the row's top or bottom edge, starting at `indent` pixels in. */
export const dropLineStyle = (edge: 'before' | 'after', indent: number, brandColour?: string): React.CSSProperties => ({
  left: indent,
  [edge === 'before' ? 'top' : 'bottom']: -1,
  backgroundColor: brandText(brandColour)
})

/**
 * Hides the scrollbar while keeping the element scrollable. The panel is
 * narrow enough that a visible track eats meaningful width and reads as
 * chrome rather than content.
 */
export const SIDEBAR_SCROLL_HIDDEN =
  '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar]:w-0'

/**
 * Indent per level of the resource tree, in pixels: a row's icon slot and the
 * gap after it (24px + 8px), less the slot's 8px before its glyph. So a row's
 * icon starts where its folder's name does, and what's in a folder reads as
 * hanging from it.
 */
export const SIDEBAR_INDENT_STEP = 24

/** Left padding for a tree row at the given depth. */
export const indentFor = (layer: number): number => 8 + layer * SIDEBAR_INDENT_STEP

/**
 * The sidebar's top row: the logo, What's new and Hide sidebar. A page header
 * beside the sidebar takes the same height, so the two read as one line across
 * the screen.
 */
export const SIDEBAR_TOP_ROW_HEIGHT = 52

/**
 * Once the sidebar is hidden, the Show sidebar button floats this far in from
 * the top and left edges, which centres the 28px button on the top row.
 */
export const SIDEBAR_RESTORE_INSET = (SIDEBAR_TOP_ROW_HEIGHT - 28) / 2

/**
 * How much room a page header leaves on its left while the sidebar is hidden:
 * the floating Show sidebar button plus the same gap again on its right, so the
 * header's first item sits beside the button rather than under it.
 */
export const SIDEBAR_RESTORE_CLEARANCE = SIDEBAR_RESTORE_INSET * 2 + 28
